import { router } from 'expo-router';
import { ArrowRight, Trophy } from 'lucide-react-native';
import { StyleSheet, View } from 'react-native';

import { Button } from '@/components/Button';
import { Card } from '@/components/Card';
import { Heading } from '@/components/Heading';
import { Overline } from '@/components/Overline';
import { Pill } from '@/components/Pill';
import { Text } from '@/components/Text';
import { colors, radii, spacing } from '@/constants/theme';

/**
 * Prize ladder, deadline and rate — Jason's launch-email numbers, matching
 * `Notifications::AugustSprint` on the backend. Hardcoded on purpose: the
 * server owns the only value that has to be right (the count), and this
 * campaign self-expires, so a config surface for a two-week contest would
 * outlive the contest.
 */
const GOAL = 10;
const PRIZE = '$1,000';
const DEADLINE = 'Ends August 31';
const RATE = 'Your first referral pays $50, every one after pays $20';

export interface SprintHomeCardProps {
  /** Completed SoFi Checking referrals inside the contest window. `0` is valid. */
  referrals: number;
  testID?: string;
}

/**
 * End of August Sprint promo for Home's pinned slot. Refer 10 friends to a free
 * SoFi Checking account by Aug 31, get $1,000 — a hand-paid contest that until
 * now only existed in the reward emails.
 *
 * Deliberately built to `FeaturedDealCard`'s geometry (same Deep Green surface,
 * `radii['2xl']` corner, Electric Green hairline, same CTA weight) because it
 * takes that card's place in the slot while the contest runs. Two different
 * shapes swapping in and out of one position reads as the page breaking.
 *
 * Whether it renders at all is `user.sprint_referrals` being a number — the
 * server nils it at midnight ET on Sept 1. Nothing here knows the date, which
 * is why the contest can end without an OTA.
 *
 * Unlike FeaturedDealCard the whole card is NOT pressable: the Button is the
 * only tap target. That sidesteps the presentational-CTA problem (a fake
 * button that has to be hidden from the a11y tree, and was a dead tab stop on
 * web until it was) rather than solving it a second time.
 */
export function SprintHomeCard({ referrals, testID }: SprintHomeCardProps) {
  const pct = Math.max(0, Math.min(100, Math.round((referrals / GOAL) * 100)));
  const counted = Math.min(referrals, GOAL);

  return (
    <Card variant="dark" padding={spacing.lg} style={styles.card} testID={testID}>
      <View style={styles.topRow}>
        <Overline tone="onDark">End of August Sprint</Overline>
        <Pill tone="green" icon={Trophy}>
          Contest
        </Pill>
      </View>

      {/* Same 7pt track/fill as AvailabilityBar on a dark surface, so the two
          cards' progress bars are the same object visually. The width is
          computed, which is what the array-style form is for. */}
      <View style={styles.track}>
        {pct > 0 && <View testID="sprint-fill" style={[styles.fill, { width: `${pct}%` }]} />}
      </View>
      <Text variant="caption" color="frost" style={styles.progressLabel}>
        {counted} of {GOAL} referrals
      </Text>

      {/* One interpolated string, not JSX children: Heading types `children`
          as string so the uppercase transform can't silently skip an element. */}
      <Heading size="2xl" tone="inverse" style={styles.title}>
        {`Refer ${GOAL} friends, get ${PRIZE}`}
      </Heading>
      <Text variant="caption" color="textOnDarkMuted" style={styles.terms}>
        {DEADLINE} · {RATE}
      </Text>

      <Button
        label="Share your link"
        trailingIcon={ArrowRight}
        fullWidth
        onPress={() => router.push('/referrals')}
        style={styles.cta}
        testID="sprint-share-cta"
      />
    </Card>
  );
}

const styles = StyleSheet.create({
  // Mirrors FeaturedDealCard.card — it occupies the same slot, so a different
  // corner or border here would show up as the page jumping when one swaps for
  // the other.
  card: {
    borderRadius: radii['2xl'],
    overflow: 'hidden',
    borderWidth: 1,
    borderColor: colors.electricGreen,
    marginTop: spacing.sm,
  },
  topRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: spacing.md,
    marginBottom: spacing.md,
  },
  track: {
    height: 7,
    borderRadius: radii.full,
    overflow: 'hidden',
    backgroundColor: colors.frostTint,
  },
  fill: {
    height: '100%',
    borderRadius: radii.full,
    backgroundColor: colors.electricGreen,
  },
  progressLabel: {
    marginTop: spacing.xs,
  },
  title: {
    marginTop: spacing.lg,
  },
  terms: {
    marginTop: spacing.xs,
  },
  cta: {
    marginTop: spacing.lg,
  },
});
